/*
 * Models for user defined KB Role
 */

define([
  "backbone",
  "messaging/messenger",
  "configuration/properties",
  "jquery",
  "underscore",
  "modules/shared/sortableList/sortableListModel",
  "modules/shared/history/historyModel",
], function (Backbone, Messenger, Properties, $, _, SortableListModel, HistoryModel) {
  var KbRoleModel = {};

  KbRoleModel.kbRolePrivilege = Backbone.Model.extend({
    idAttribute: "id",
  });

  KbRoleModel.kbRolePrivileges = Backbone.Collection.extend({
    model: KbRoleModel.kbRolePrivilege,
    backup: function () {
      this._backup();
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    restore: function () {
      //restore backup by resetting the collection using the backup's models
      this.reset(this.backupCol.models);
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      var col = new KbRoleModel.kbRolePrivileges();
      this.each(function (model) {
        col.add(model.clone());
      });
      this.backupCol = col;
    },
  });

  KbRoleModel.kbRole = Backbone.Model.extend({
    idAttribute: "id",
    defaults: {
      description: "",
      id: null,
      kbId: 0,
      kbName: "",
      name: "",
      tenantId: 0,
      userCount: 0,
    },
    defaultValidateAttributes: ["name"],
    url: function () {
      var url = Properties.rootURL + "/" + this.get("kbId") + "/kbRole";
      var id = this.get("id");
      if (id) {
        url += "/" + id;
      }
      return url;
    },
    parse: function (resp) {
      if (resp && resp.hasOwnProperty("privileges")) {
        var privileges = [];
        _.each(
          resp.privileges,
          function (privilege) {
            var model = new KbRoleModel.kbRolePrivilege(privilege);
            privileges.push(model);
          },
          this,
        );
        this.privileges().reset(privileges);
        delete resp.privileges;
      }
      return resp;
    },
    saveModel: function (options) {
      // Save the privileges along with the role
      const modelOptions = this._prepareSaveModelOptions(options);
      const originalSuccess = modelOptions.success;
      let shouldSave = this._allowSave(options);
      const privileges = [];
      this.privileges().each(function (privilege) {
        privileges.push(privilege.toJSON(options));
        if (privilege.isNew() || privilege.dirty) {
          shouldSave = true;
        }
      });
      if (!shouldSave) {
        originalSuccess(this, this.toJSON(options), options);
        return;
      }
      const model = this.clone();

      model.set("privileges", privileges);
      modelOptions.success = (model, resp) => {
        const newAttributes = this.parse(resp);
        this.set(newAttributes);
        this.dirty = false;
        originalSuccess(this, newAttributes, options);
      };
      model.save(null, modelOptions);
    },

    history: function () {
      var history = new HistoryModel.historyCollection();
      if (!this.isNew()) {
        var url = this.url() + "/history";
        history.url = function () {
          return url;
        };
        history.fetch();
      }
      return history;
    },

    //This is how we implement sub-models.  The privileges sub-model will reside in this.privilegesCol, be
    //of type UsersModel.kbRolePrivileges, and is embedded in the JSON to/from the server in a privileges array
    privileges: function () {
      this.privilegesCol = this.getSubModel(
        this.privilegesCol,
        KbRoleModel.kbRolePrivileges,
        null,
        false,
        true,
      );
      return this.privilegesCol;
    },

    backup: function () {
      this._backup();
      //backup the collection into a backup variable once the collection is synced
      this.onSynced(function () {
        this._backup();
      }, this);
    },
    restore: function () {
      //restore backup by reseting the collection using the backup's models
      this.set(this.cloneCopy.attributes);
      _.each(
        this.subModels,
        function (subModel) {
          subModel.restore();
        },
        this,
      );
      //set the dirty flag back to what it was before the backup
      this.dirty = this.wasDirty;
    },
    _backup: function () {
      //save whether the example was dirty at the time
      this.wasDirty = this.dirty;
      //create a new collection and add cloned models to that collection
      this.cloneCopy = this.clone();

      _.each(
        this.subModels,
        function (subModel) {
          subModel.backup();
        },
        this,
      );
    },
    validateAttribute: function (attribute, value) {
      if (attribute === "name") {
        return (
          this.validateRequiredValue(value, {
            emptyValueMessage: "admin.kbRoleAdmin.noName",
            maxLength: 255,
          }) || this.validateSafeCharacters(value)
        );
      }
    },
  });

  KbRoleModel.tenantKbRoles = Backbone.Collection.extend({
    model: KbRoleModel.kbRole,
    initializeCustom: function (tenantId) {
      this.tenantId = tenantId;
    },
    url: function () {
      return `${Properties.adminRootURL}/tenants/${this.tenantId}/kbRoles`;
    },
    addNewKbRole: function (kbId) {
      var currentUser = Messenger.reqres.request("user");
      var newKbRole = new KbRoleModel.kbRole({
        createDate: new Date(),
        createUserId: currentUser.get("userId"),
        createUserName: currentUser.get("userName"),
        kbId: kbId,
        lastUpdateDate: new Date(),
        lastUpdateUserId: currentUser.get("userId"),
        lastUpdateUserName: currentUser.get("userName"),
        tenantId: this.tenantId,
      });
      this.add(newKbRole);
      return newKbRole;
    },
  });

  var tenantKbRolesMap = {};
  Messenger.reqres.setHandler("tenant:kbRoles", function (tenantId) {
    if (!tenantId) {
      return null;
    }
    var tenantKbRoles = tenantKbRolesMap[tenantId];
    if (!tenantKbRoles) {
      tenantKbRoles = new KbRoleModel.tenantKbRoles(tenantId);
      tenantKbRoles.fetch();
      tenantKbRolesMap[tenantId] = tenantKbRoles;
    }
    return tenantKbRoles;
  });

  Messenger.reqres.setHandler("kbRole:list:headers", function () {
    var Header = SortableListModel.headerModel;
    return new Backbone.Collection([
      new Header({
        headerName: $.t("admin.kbRoleAdmin.name"),
        modelAttribute: "name",
        selectable: true,
        sortable: true,
        longTextField: true,
        filterable: true,
      }),
      new Header({
        headerName: $.t("admin.kbRoleAdmin.kbName"),
        modelAttribute: "kbName",
        selectable: false,
        sortable: true,
        longTextField: true,
        filterable: true,
      }),
      new Header({
        headerName: $.t("admin.kbRoleAdmin.description"),
        modelAttribute: "description",
        selectable: false,
        sortable: true,
        longTextField: true,
        filterable: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.kbRoleAdmin.userCount"),
        modelAttribute: "userCount",
        selectable: false,
        sortable: true,
        numberField: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.kbRoleAdmin.createDate"),
        modelAttribute: "createDate",
        dateField: true,
        dateOnly: true,
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.kbRoleAdmin.createUserName"),
        modelAttribute: "createUserName",
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
        filterable: true,
        textField: true,
      }),
      new Header({
        headerName: $.t("admin.kbRoleAdmin.lastUpdateDate"),
        modelAttribute: "lastUpdateDate",
        dateField: true,
        dateOnly: true,
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
      }),
      new Header({
        headerName: $.t("admin.kbRoleAdmin.lastUpdateUserName"),
        modelAttribute: "lastUpdateUserName",
        selectable: false,
        sortable: true,
        hidden: true,
        hideable: true,
        filterable: true,
        textField: true,
      }),
      new Header({
        headerName: "",
        modelAttribute: "name",
        width: "15px",
        customHtml:
          '<button type="button" class="btn btn-danger btn-mini deleteKbRole"\
             data-i18n="[title]admin.kbRoleAdmin.deleteKbRole" \
             data-toggle="tooltip"><i class="icon-remove"></i></button>',
      }),
    ]);
  });

  //Request for the headers of the topic examples history list
  Messenger.reqres.setHandler("kbRole:history:headers", function (headerType) {
    var Header = SortableListModel.headerModel;
    if (headerType === "detailModal") {
      return new Backbone.Collection([
        new Header({
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          textField: true,
        }),
        new Header({
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          textField: true,
        }),
      ]);
    } else {
      return new Backbone.Collection([
        new Header({
          headerName: $.t("history.user"),
          modelAttribute: "userName",
          sortable: true,
          filterable: true,
          textField: true,
          width: "15%",
        }),
        new Header({
          headerName: $.t("history.date"),
          modelAttribute: "dateTime",
          defaultSort: true,
          defaultReversed: true,
          sortable: true,
          dateField: true,
        }),
        new Header({
          headerName: $.t("history.change"),
          modelAttribute: "changeType",
          sortable: true,
          filterable: true,
          textField: true,
          width: "15%",
        }),
        new Header({
          headerName: $.t("history.oldValue"),
          modelAttribute: "oldValue",
          sortable: true,
          textField: true,
        }),
        new Header({
          headerName: $.t("history.newValue"),
          modelAttribute: "newValue",
          sortable: true,
          textField: true,
        }),
      ]);
    }
  });

  Messenger.reqres.setHandler("kbRole:data", function (kbRoleId) {
    var kbRole = new KbRoleModel.kbRole();
    kbRole.set("id", kbRoleId);
    kbRole.fetch();
    return kbRole;
  });

  return KbRoleModel;
});
